import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { MobileNavigation } from "@/components/MobileNavigation";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LogOut, Trash2 } from "lucide-react";

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSignOut = async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    console.log("Starting logout process...");
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Logout error:", error);
        // Handle user_not_found error
        if (error.message?.includes('user_not_found')) {
          console.log("User not found, clearing all auth state");
          localStorage.clear();
          window.location.href = "/";
          return;
        }
        throw error;
      }
      
      console.log("Logout successful");
      localStorage.clear(); // Clear all local storage
      window.location.href = "/"; // Force a full page reload
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
    } catch (error) {
      console.error("Error during logout:", error);
      toast({
        title: "Error",
        description: "There was a problem logging out",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    console.log("Starting account deletion process...");

    try {
      // Call the delete_user RPC function
      const { error: deleteError } = await supabase.rpc('delete_user');
      
      if (deleteError) {
        console.error("Delete error:", deleteError);
        // Handle user_not_found error
        if (deleteError.message?.includes('user_not_found')) {
          console.log("User not found, clearing all auth state");
          localStorage.clear();
          window.location.href = "/";
          return;
        }
        throw deleteError;
      }

      // Clear all local storage and auth state
      localStorage.clear();
      
      console.log("Account deleted successfully");
      
      // Force a full page reload and redirect to home
      window.location.href = "/";
      
      toast({
        title: "Account deleted",
        description: "Your account and all associated data have been permanently deleted",
      });
    } catch (error) {
      console.error("Error during account deletion:", error);
      toast({
        title: "Error",
        description: "There was a problem deleting your account",
        variant: "destructive",
      });
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="container mx-auto py-8 pb-20 md:pb-8">
        <h1 className="text-4xl font-bold mb-8">{t('nav.settings')}</h1>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.account')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="outline"
                  className="gradient-button-variant transition-all duration-400 hover:scale-[1.02] w-full sm:w-auto"
                  onClick={handleSignOut}
                  disabled={isLoggingOut}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {isLoggingOut ? t('settings.signingOut') : t('settings.signOut')}
                </Button>
                <LanguageToggle />
              </div>
            </CardContent>
          </Card>

          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">{t('settings.dangerZone')}</CardTitle>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive"
                    className="w-full sm:w-auto"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {isDeleting ? t('settings.deletingAccount') : t('settings.deleteAccount')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('settings.deleteConfirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('settings.deleteConfirmDescription')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t('settings.deleteAccount')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
      </div>
      <MobileNavigation />
    </>
  );
};

export default Settings;